import { syncRouterFromWindow } from './routerStore';

let writeHistoryDepth = 0;
let writesThisTick = 0;
let lastWriteTime = 0;
let writesMicroScheduled = false;

const isDev = Boolean(import.meta.env?.DEV);

export function isInsideWriteHistory(): boolean {
  return writeHistoryDepth > 0;
}

function scheduleWritesThisTickReset(): void {
  if (writesMicroScheduled) return;
  writesMicroScheduled = true;
  queueMicrotask(() => {
    writesThisTick = 0;
    writesMicroScheduled = false;
  });
}

export type WriteHistoryOptions = {
  /** Set false for `history.back()` / `go()` where the URL updates on `popstate`, not synchronously. */
  syncAfter?: boolean;
};

/**
 * Single writer entry: all `pushState` / `replaceState` must run inside `fn`.
 * After `fn`, syncs the router store from `window.location` unless `syncAfter: false` (e.g. `back()`).
 */
export function writeHistory(fn: () => void, options?: WriteHistoryOptions): void {
  const syncAfter = options?.syncAfter !== false;
  writeHistoryDepth += 1;
  try {
    fn();
  } finally {
    writeHistoryDepth -= 1;
  }

  if (syncAfter && typeof window !== 'undefined') {
    syncRouterFromWindow();
  }

  if (isDev && typeof window !== 'undefined') {
    writesThisTick += 1;
    scheduleWritesThisTickReset();
    if (writesThisTick > 1) {
      // eslint-disable-next-line no-console
      console.error('[navigation] Multiple history writes in the same macrotask — use one writeHistory batch');
    }
    const now = performance.now();
    if (lastWriteTime > 0 && now - lastWriteTime < 16) {
      // eslint-disable-next-line no-console
      console.warn('[navigation] Multiple history writes within 16ms — check await / batched navigation');
    }
    lastWriteTime = now;
  }
}

let devGuardsInstalled = false;

/** DEV-only: throw if `pushState`/`replaceState` run outside `writeHistory`. */
export function installDevHistoryGuards(): void {
  if (!isDev || devGuardsInstalled || typeof window === 'undefined') return;
  devGuardsInstalled = true;

  const originalPush = window.history.pushState.bind(window.history);
  const originalReplace = window.history.replaceState.bind(window.history);

  window.history.pushState = ((data: unknown, unused: string, url?: string | URL | null) => {
    if (!isInsideWriteHistory()) {
      throw new Error(
        'Unauthorized history.pushState outside writeHistory — use writeHistory(), AppRouter.navigate(), or appNavigation.openSheet/updateSheet',
      );
    }
    return originalPush(data, unused, url as never);
  }) as typeof window.history.pushState;

  window.history.replaceState = ((data: unknown, unused: string, url?: string | URL | null) => {
    if (!isInsideWriteHistory()) {
      throw new Error(
        'Unauthorized history.replaceState outside writeHistory — use writeHistory(), AppRouter.navigate(), or appNavigation.openSheet/updateSheet',
      );
    }
    return originalReplace(data, unused, url as never);
  }) as typeof window.history.replaceState;
}
